import { css, keyframes } from '@emotion/react'
import styled, { StyledComponent } from '@emotion/styled'
import { transparentize } from 'polished'
import { HTMLAttributes } from 'react'

import { Flex, FlexBoxProps } from '../Flex'
import { Theme } from '../Theme'
import { themeColor } from '../Theme/utils'

type CardBorderProps = {
  border?: boolean | string
}
const cardBorder = ({
  border,
  theme,
}: CardBorderProps & { bg?: any; theme: Theme }) => {
  if (!border) return undefined
  const borderColorName = border === true ? 'border' : border
  const color = themeColor(borderColorName, theme)
  return `border: ${transparentize(0.7, color)} 1px solid;`
}

type CardShadowProps = {
  shadow?: boolean | string
}
const cardShadow = ({ shadow, theme }: CardShadowProps & { theme: Theme }) => {
  if (!shadow) return undefined
  const shadowColorName = shadow === true ? 'shadow' : shadow
  const color = themeColor(shadowColorName, theme)
  if (!color)
    return `box-shadow: ${transparentize(0.3, shadowColorName)} 0 8px 24px;`
  return `box-shadow: ${transparentize(0.7, color)} 0 8px 24px;`
}

type CardLoadingProps = {
  showSkeleton?: boolean
}

const shineKeyframe = keyframes`
  to {
    background-position: right -40px top 0;
  }
`

const cardLoadingSkeleton = ({
  showSkeleton,
}: CardLoadingProps & { theme: Theme }) => {
  if (!showSkeleton) return undefined
  return css`
    background-color: #e2e5e7;
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0)
    );
    background-size: 40px 100%;
    background-repeat: no-repeat;
    background-position: left -40px top 0;
    animation: ${shineKeyframe} 1s ease infinite;
  `
}

export type CardProps = FlexBoxProps &
  CardBorderProps &
  CardShadowProps &
  CardLoadingProps
export const Card: StyledComponent<
  CardProps,
  HTMLAttributes<HTMLElement>
> = styled(Flex)`
  border-radius: ${({ theme }) => theme.card.borderRadius}px;
  ${cardBorder}
  ${cardShadow}
  ${cardLoadingSkeleton}
`
Card.displayName = 'Card'
